/*
 * Written by Alexander Agudelo < alex@kemu.io >, 2020
 * Date: 29/Nov/2020
 * Last Modified: 26/11/2025, 3:50:48 pm
 * Modified By: Alexander Agudelo
 * Description:  Helper functions to manipulate the cached recipe. This module
 * does NOT perform any type of UI or state changes, it modifies the recipe cache directly and
 * accesses Kemu Core functionality
 * 
 * ------
 * Copyright (C) 2020 Kemu - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential.
 */

import { customAlphabet } from 'nanoid';
import {
	findBlockInRecipe,
	findThingInRecipe,
	findWidgetInRecipe,
	findRecipe,
} from '@kemu-io/kemu-core/common/recipeCache';
import { serializeGlobalVariables, deserializeGlobalVariables } from '@kemu-io/kemu-core/common/lib/utils/globalVarsUtils.js';
import globalVariablesManager from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import kemuCore from '@kemu-io/kemu-core';
import {
	BlockAction,
	BlockCanvasInfo,
	BlockInput,
	ChildBlock,
	Position,
	RecipeBlock,
	BlockState,
	BlockPort,
	CustomWidgetState,
	DynamicFieldBaseConfig,
	GateCanvasInfo,
	GateChild,
	GateState,
	WidgetType,
	PortType,
	RecipeWidget,
	// WidgetPortIdentifier,
	WidgetPortIdentifierWithName,
	WidgetState,
	CachedRecipe,
	RecipeCanvasInfo,
	RecipeStorage,
	WidgetGroupState,
	Recipe,
	OriginLessRecipe,
	CachedRecipeDbInfo,
} from '@kemu-io/kemu-core/types';
import GatesManager from '@kemu-io/kemu-core/widgetsManager';
// import BlocksManager from '@kemu-io/kemu-core/dist/blocks/manager';
// import * as LogicProcessor from '@kemu-io/kemu-core/processor';
import {
	ThingCategory,
	ThingType,
	CustomWidgetVariant,
} from '@kemu-io/kemu-types';
import variablesManager, { getControlScopedVarName } from '@kemu-io/kemu-core/common/managers/variablesManager.js';
import {
	addToStorageUnit,
	getStorageUnitFromKey,
	removeStorageUnit,
	removePrivateProperties,
	decodeWidgetPortIdentifier,
	decodeType,
} from '@kemu-io/kemu-core/common/utils';
import { KEMU_ID_MARK } from '@kemu-io/kemu-core/common/constants';
import { RemoveInvokeError } from '@kemu-io/hs/ri';
import { Data, PartialData, WidgetSecretConfig, SupportedTypes } from '@kemu-io/hs-types';
import { WidgetCollectionItem } from '../reducers/widget/widgetSlice';
import { WidgetsMap } from '@src/types/core_t';
import { generateDomId, jsonCloneObject, safeJsonClone } from '@common/utils';
import { CANVAS_DRAGGABLE_CLASS, LM_CANVAS_CONTAINER_CLASS, LOGIC_MAPPER_CANVAS_CLASS } from '@common/constants';
import { ParsedWidgetContents } from '@src/types/widgets';
import { RecipeMetaFields } from '@src/types/recipe_t';
import Logger from '@common/logger';
import { createRecipeUuid } from '@src/features/interface/interfaceApi';

const logger = Logger('recipeUtils');

/** 
 * Generates consistent and unique block id within the recipe scope
 * @param recipeId is the id of the recipe where the block will be located.
 * It is used to search for existing blocks and make sure no 2 IDs are equal
 **/
const _getUniqueBlockId = (recipe: CachedRecipe) => {
	// Make sure IDs are unique within the recipe
	const len = 4;
	let id = getRandomId(len);
	while (recipe.blocks[id]) { id = getRandomId(len); }
	// Generate an id like: {#}ABCD
	// the {#} is used to identify references to this block and easily update them
	// during merge
	return `${KEMU_ID_MARK}${id}`;
};

/**
 * Uses the last block in the recipe to determine where the new block should be added
 * @param poolId the id of the recipe in the pool
 * @param recipe the actual recipe contents
 */
const _getDefaultBlockPosition = (poolId: string, recipe: CachedRecipe): Position => {
	const blockKeys = Object.keys(recipe.blocks);
	if (blockKeys.length) {
		const lastAddedBlock = recipe.blocks[blockKeys[blockKeys.length-1]];
		const nextPosition = _getNextBlockPosition(poolId, lastAddedBlock);
		return nextPosition;
	} else {
			// The first block
		return {
			x: 200,
			y: 200
		};
	}
};

/**
 * Adds a new block to the recipe
 * @param recipeId the id of the recipe in the pool
 * @param blockInfo information about the block from the DB
 * @param allowOnlyOneType If true it will not add a new block if there is one that shares the same
 * dbId and version number.
 * @returns a reference of the newly added block. IMPORTANT: Mutating this reference
 * WILL mutate the original object.
 */
// const addBlock = (recipeId: string, blockInfo: LimitedThingInfo, allowOnlyOneType=false): RecipeBlock | null => {
// 	const recipe = findRecipe(recipeId);
// 	if (!recipe) { throw new Error(`Recipe [${recipeId} does not exist]`); }
// 	const position = _getDefaultBlockPosition(recipeId, recipe);
// 	const id = _getUniqueBlockId(recipe);

// 	// Make sure the block doesn't exist
// 	// FIXME: Write unit test
// 	if (allowOnlyOneType) {
// 		const existingBlock = Object.keys(recipe.blocks).find(brId => recipe.blocks[brId].id === blockInfo.id && recipe.blocks[brId].version === blockInfo.version);
// 		if (existingBlock) {
// 			console.error(`Block ${blockInfo.id} with version ${blockInfo.version} already exists in the recipe. Aborting`);
// 			return null;
// 		}
// 	}

// 	const blockModule = BlocksManager.getThingWebProcessor(blockInfo.id, blockInfo.version);

// 	const inputs = blockModule.getInputPortsNames && blockModule.getInputPortsNames() || [];
// 	const outputs = blockModule.getOutputPortsNames && blockModule.getOutputPortsNames() || [];

// 	const block: RecipeBlock = {
// 		canvas: { logicMaker: { position: { x: 0, y: 0 } }, position },
// 		category: blockInfo.category as ThingCategory,
// 		children: [],
// 		gates: {},
// 		id: blockInfo.id,
// 		inputPorts: _getUniqueBlockPorts(inputs, id, recipeId),
// 		outputPorts: _getUniqueBlockPorts(outputs, id, recipeId),
// 		name: blockInfo.name,
// 		state: {},
// 		type: blockInfo.type,
// 		version: blockInfo.version,
// 		recipeId: id
// 	};

// 	recipe.blocks[id] = block;
// 	return block;
// };

/**
 * Removes a block from the recipe and all references to it from parent blocks.
 * This method also deletes all storage units that belong to the block.
 * @param recipeId the recipe pool id
 * @param blockId the id of the block in the recipe
 * @returns a dictionary of the remaining blocks in the recipe. 
 * WARNING: Mutating the returned reference WILL mutate the cached recipe object
 */
// const removeBlock = (recipeId: string, blockId: string): BlockMap => {
// 	const recipe = findRecipe(recipeId);
// 	if (!recipe) { throw new Error(`Recipe [${recipeId} does not exist]`); }

// 	const block = findBlockInRecipe(recipeId, blockId);
// 	if (!block) { throw new Error(`Block ${blockId} does not exist in recipe ${recipeId}`); }

// 	// Delete references from parents
// 	for (const parent in recipe.blocks) {
// 		const foundAtIndex = (recipe.blocks[parent].children || []).findIndex(child => child.blockId === blockId);
// 		if (foundAtIndex !== -1) {
// 			recipe.blocks[parent].children.splice(foundAtIndex, 1);
// 		}
// 	}

// 	// Delete storage references
// 	if (block.storageUnits && recipe.storage) {
// 		for (const unitId of block.storageUnits) {
// 			delete recipe.storage[unitId];
// 		}
// 	}

// 	// Delete the actual block
// 	delete recipe.blocks[blockId];
// 	return recipe.blocks;
// };

/**
 * Finds the best location for a block. By default, th
 * @param recipe a reference to the recipe
 * @param sourceBlock if provided, the next location will be 
 * 'close' to the given source. Otherwise, the next location will be 'close'
 * to the last block in the recipe
 */
const _getNextBlockPosition = (recipeId: string, sourceBlock: RecipeBlock) => {
	let BLOCK_WIDTH = 130;
	const leftPadding = 50;		// distance from the right of the last block
	const blockDomEl = getEntityDomEl(recipeId, sourceBlock.recipeId);
	if (blockDomEl) {
		BLOCK_WIDTH = blockDomEl.getBoundingClientRect().width || BLOCK_WIDTH;
	}

	// TODO: Make sure the new position is not outside the view port, if so, choose a different position.
	return {
		// Add them horizontally
		x: sourceBlock.canvas.position.x + BLOCK_WIDTH + leftPadding,
		y: sourceBlock.canvas.position.y,
	};
};

/***
 * Clones an existing block excluding children. It also clones the storage units contents
 * @param recipeId the id of the recipe in the pool
 * @param blockRecipeId the id of the block in the recipe (recipeId property)
 * @returns a reference to the newly added block. IMPORTANT: modifying the returned reference
 * WILL modify the stored object.
 */
// const duplicateBlock = (recipeId: string, blockRecipeId: string): RecipeBlock => {
// 	const recipe = findRecipe(recipeId);
// 	if (!recipe) { throw new Error(`Recipe [${recipeId} does not exist]`); }
// 	const sourceBlock = findBlockInRecipe(recipeId, blockRecipeId);
// 	if (!sourceBlock) { throw new Error(`Block ${blockRecipeId} does not exist in recipe ${recipeId}`); }

// 	// Clone everything, specially the 'state'
// 	const cleanCopy = safeJSONStringify(sourceBlock);
// 	const clonedBlock: RecipeBlock = JSON.parse(cleanCopy);
// 	const id = _getUniqueBlockId(recipe);

// 	const blockModule = BlocksManager.getThingWebProcessor(clonedBlock.id, clonedBlock.version);

// 	// Extract the port names so we can generate unique ones using the standard method
// 	const inputNames = blockModule.getInputPortsNames && blockModule.getInputPortsNames() || [];
// 	const outputNames = blockModule.getOutputPortsNames && blockModule.getOutputPortsNames() || [];

// 	// Replace values
// 	clonedBlock.recipeId = id;
// 	clonedBlock.canvas.position = _getNextBlockPosition(recipeId, sourceBlock);
// 	clonedBlock.children = [];
// 	clonedBlock.storageUnits = [];
// 	clonedBlock.inputPorts = _getUniqueBlockPorts(inputNames, id, recipeId);
// 	clonedBlock.outputPorts = _getUniqueBlockPorts(outputNames, id, recipeId);

// 	if (recipe.storage) {
// 		let stateStr = JSON.stringify(clonedBlock.state);
// 		for (const oldStorageId of sourceBlock.storageUnits || []) {
// 			const storageSource = recipe.storage[oldStorageId];
// 			const newDiskId = nanoid(4);
// 			const markedId = `${KEMU_ID_MARK}${newDiskId}`;
// 			const diskCopy = new Uint8Array(storageSource);
// 			// Add copied data to storage
// 			recipe.storage[markedId] = diskCopy;
// 			// Add reference to cloned instance
// 			clonedBlock.storageUnits.push(markedId);
// 			// Update the state of the block so that any reference to the modify keys can be updated
// 			stateStr = replaceMarkedIds(stateStr, oldStorageId, newDiskId);
// 		}

// 		// Restore the block state
// 		clonedBlock.state = JSON.parse(stateStr);
// 	}



// 	recipe.blocks[id] = clonedBlock;
// 	return clonedBlock;
// };

/**
 * Registers a block as a child in the recipe. This method does NOT handle
 * any type of UI connections or events.
 * @param recipeId the recipe pool id
 * @param source the id of the parent block in the recipe (recipeId)
 * @param target the id of the block in the recipe (recipeId) to add as a child
 * @param sourcePort the id of the source port (current block)
 * @param targetPort the id of the port in the `target` to attach the connection to
 * @returns an updated reference of the list of children of the parent block. IMPORTANT: Mutating
 * the returned reference WILL mutate the original object
 */
const registerChildBlock = (recipeId: string, source: string, target: string, sourcePort: string, targetPort: string): ChildBlock[] => {
	const block = findBlockInRecipe(recipeId, source);
	if (!block) { throw new Error(`Block ${source} does not exist in recipe ${recipeId}`); }

	const child = {
		blockId: target,
		sourcePort,
		targetPort
	};

	block.children.push(child);

	return block.children;
};

/**
 * Removes an existing child from a block in the recipe. This method does NOT handle
 * any type of UI connections or events.
 * @param recipeId the recipe pool id
 * @param source the id of the parent block in the recipe (recipeId)
 * @param target the id of the block in the recipe (recipeId) to add as a child
 * @param sourcePort the id of the source port (current block)
 * @param targetPort the id of the port in the `target` to attach the connection to
 * @returns an updated reference of the list of children of the parent block. IMPORTANT: Mutating
 * the returned reference WILL mutate the original object
 */
const removeChildBlock = (recipeId: string, source: string, target: string, sourcePort: string, targetPort: string): ChildBlock[] => {
	const block = findBlockInRecipe(recipeId, source);
	if (!block) { throw new Error(`Block ${source} does not exist in recipe ${recipeId}`); }

	const index = block.children.findIndex(child => child.blockId === target && child.sourcePort === sourcePort && child.targetPort === targetPort);
	if (index !== -1) {
		block.children.splice(index, 1);
	}

	return block.children;
};


/**
 * Registers a gate as a child. This method does NOT handle
 * any type of UI connections or events.
 * @param recipeId the recipe pool id
 * @param blockId the id of the block (recipeId) the gate is located in
 * @param source the id of the parent gate in the recipe (recipeId)
 * @param target the id of the gate in the recipe (recipeId) to add as a child
 * @param sourcePort the id of the source port (current gate)
 * @param targetPort the id of the port in the `target` to attach the connection to
 * @returns an updated reference to the list of children of the parent gate. IMPORTANT: Mutating the reference
 * WILL mutate the original object
 */
const registerChildGate = (recipeId: string, blockId: string, source: string, target: string, sourcePort: string, targetPort: string): GateChild[] => {
	const block = findBlockInRecipe(recipeId, blockId);
	if (!block) { throw new Error(`Block ${blockId} does not exist in recipe ${recipeId}`); }

	const parentGate = block.gates[source];
	const childGate = block.gates[target];
	if (parentGate && childGate) {
		parentGate.children.push({
			childId: target,
			sourcePort: (sourcePort as WidgetPortIdentifierWithName),
			targetPort: (targetPort as WidgetPortIdentifierWithName)
		});
	}

	return parentGate.children;
};


/**
 * Checks whether the target gate is already attached to the same port of the source gate
 * @param recipeId the recipe pool id
 * @param blockId the id of the block (recipeId) the gate is located in
 * @param source the id of the parent gate in the recipe (recipeId)
 * @param target the id of the gate in the recipe (recipeId) to add as a child
 * @param sourcePort the id of the source port (current gate)
 * @param targetPort the id of the port in the `target` to attach the connection to
 */
const isGateAChildAtPort = (recipeId: string, blockId: string, source: string, target: string, sourcePort: string, targetPort: string): boolean => {
	const block = findBlockInRecipe(recipeId, blockId);
	if (!block) { throw new Error(`Block ${blockId} does not exist in recipe ${recipeId}`); }

	const alreadyAChild = block.gates[source].children.find(child => {
		return (
			child.childId === target &&
			child.sourcePort === sourcePort &&
			child.targetPort === targetPort
		);
	});

	return !!alreadyAChild;
};

// type WidgetPortIdentifierWithName = `input_${number}${string}` | `output_${number}${string}` | `innerInput_${number}${string}` | `innerOutput_${number}${string}`
// type WidgetPortIdentifierWithName = `input_${string}` | `output_${string}` | `innerInput_${string}` | `innerOutput_${string}`
// type WidgetPortIdentifierWithName = `${string}_input_${string}` | `${string}_output_${string}` | `${string}_innerInput_${string}` | `${string}_innerOutput_${string}`

// /**
//  * 
//  * @param portName a port with format `{type}_{index}_{name}` where type is either `input` or `output`
//  * @returns 
//  */
// const getPortWithoutName = (portName: string) => {
// 	const sp = portName.split('_');
// 	if (sp.length < 2) { return portName; }

// 	return createWidgetPortIdentifier(sp[0] as PortType, Number(sp[1]));
// };

/**
 * Removes an existing child widget from its parent. This method does NOT handle
 * any type of UI connections or events.
 * @param recipeId the recipe pool id
 * @param thingId the id of the thing (recipeId) the widget belongs to.
 * @param source the id of the parent widget in the recipe (recipeId)
 * @param target the id of the widget in the recipe (recipeId) to remove as a child
 * @param sourcePort the id of the source port (current widget)
 * @param targetPort the id of the port in the `target` to attach the connection to
 * @returns an updated reference to the list of children of the parent widget. IMPORTANT: Mutating the reference
 * WILL mutate the original object
 */
const removeChildGate = (recipeId: string, thingId: string, source: string, target: string, sourcePort: string, targetPort: string): GateChild[] => {
	const thing = findThingInRecipe(recipeId, thingId);
	if (!thing) { throw new Error(`Thing ${thingId} does not exist in recipe ${recipeId}`); }

	const parentGate = thing.gates[source];
	const childGate = thing.gates[target];
	if (parentGate && childGate) {
		let index = parentGate.children.findIndex(child => child.childId === target && child.sourcePort === sourcePort && child.targetPort === targetPort);
		// This might be an old recipe (before ports v2 prefix) so we try to match based on the old format
		if (index === -1) {
			index = parentGate.children.findIndex(child =>
				child.childId === target
				// && child.sourcePort === getPortWithoutName(sourcePort)
				// && child.targetPort === getPortWithoutName(targetPort)
				&& child.sourcePort === sourcePort
				&& child.targetPort === targetPort
			);
		}

		if (index !== -1) {
			parentGate.children.splice(index, 1);
		}
	}

	return parentGate.children;
};

/**
 * A performant method to get all the widgets that reside inside a given widget, including
 * nested widgets (widgets inside of widgets inside of widgets...)
 * @param recipeId the id of the recipe in the pool
 * @param thingId the id of the thing (recipeId) the widget belongs to.
 * @param widgetId the id of the widget in the recipe to get the children from
 * @returns an array with all the widgets that reside inside the given widget.
 * 
 * IMPORTANT: This method returns the actual reference to the widgets, so modifying them will
 * MUTATE the original object.
 */
const getGroupInnerWidgets = (recipeId: string, thingId: string, widgetId: string): RecipeWidget[] => {
	const thing = findThingInRecipe(recipeId, thingId);
	if (!thing) { return []; }

	const thisWidget = thing.gates[widgetId];
	if (!thisWidget) { return []; }

	const inside: RecipeWidget[] = [];
	const widgets = thing.gates;
	for (const wId in widgets) {
		const widget = widgets[wId];
		const directlyInside = widget.groupId === widgetId;
		// Check for nested widgets
		if (directlyInside) {
			// If a group, include its inner widgets
			if (widget.type === WidgetType.widgetGroup) {
				const innerWidgets = getGroupInnerWidgets(recipeId, thingId, widget.id);
				inside.push(...innerWidgets);
			} else {
				inside.push(widget);
			}
		}
	}

	return inside;
};

/**
 * @param recipeId the recipe pool id
 * @param blockId the id of the block (recipeId) the gate is located in
 * @param widgetId id of the parent widget
 * @param sourcePort id of the source port children are connected to
 * @returns a list of widgets connected to the given port
 */
const getChildrenWidgets = (
	recipeId: string,
	blockId: string,
	widgetId: string,
	sourcePort: WidgetPortIdentifierWithName
): GateChild[] => {
	const widget = findWidgetInRecipe(recipeId, blockId, widgetId);
	if (!widget) { throw new Error(`Widget ${widgetId} does not exist in block ${blockId} and recipe ${recipeId}`); }
	const children = widget.children.filter(child => child.sourcePort === sourcePort);
	return children;
};

/** 
 * Generates consistent and unique gate id within the block scope
 * @param thing a reference to the target Thing the generated widget id will
 * be placed into. This is used to make sure no other widget exists
 **/
const generateUniqueWidgetId = (thing: RecipeBlock): string => {
	// Make sure IDs are unique within the block
	const len = 4;
	let id = getRandomId(len);
	while (thing.gates[id]) { id = getRandomId(len); }
	// Generate an id like: {#}ABCD
	// the {#} is used to identify references to this gate and easily update them
	// during merge
	return `${KEMU_ID_MARK}${id}`;
};

/**
 * @deprecated use `generateUniqueWidgetId` instead
 */
const generateUniqueGateId = generateUniqueWidgetId;

/**
 * Determines the position of a gate based on the last one added.
 * TODO: Position widgets based on their type (inputs on the left, actions on the right)
 * 
 * @param recipeId the id of the recipe in the pool
 * @param thingId the id of the thing in the recipe
 * @param type the type of gate being added
 * @param gatesMap a list of other gates already added to the thing. It will be used to calculate
 * the best position for the new gate
 */
const getDefaultWidgetPosition = (recipeId: string, thingId: string, type: WidgetType, widgetsMap: Record<string, RecipeWidget>): Position => {
	type GateDimensions = {cx: number, cy: number, left: number, top: number, w: number, h: number};
	const defaultX = 100;
	const defaultY = 100;
	const paddingBottom = 10;

	const gatesKeys = Object.keys(widgetsMap || {});
	let lastGateDimensions: GateDimensions | null = null;

	if (gatesKeys.length) {
		const lastAddedGate = widgetsMap[gatesKeys[gatesKeys.length-1]];
		const lastGateDomEl = getEntityDomEl(recipeId, thingId, lastAddedGate.id);

		if (lastGateDomEl) {
			const boundingBox = lastGateDomEl.getBoundingClientRect();

			lastGateDimensions = {
				cx: lastAddedGate.canvas.position.x,			// Absolute positioning
				cy: lastAddedGate.canvas.position.y,
				left: boundingBox.left,
				top: boundingBox.top,
				w: boundingBox.width,
				h: boundingBox.height,
			};
		}
	}

	if (lastGateDimensions) {
		return {
			// Horizontally
			x: lastGateDimensions.cx,
			y: lastGateDimensions.cy + lastGateDimensions.h + paddingBottom
		};
	} else {
		return {
			x: defaultX,
			y: defaultY
		};
	}
};


// /**
//  * Translate the source or target port properties of a widget children link.
//  * @param childPort the id of the port with or without the widget id (Eg: input_0 OR {#}widget123_input_name);
//  * 
//  */
// const decodeWidgetPortIdentifier = (childPort: string): {
// 	portType: PortType,
// 	widgetId: string,
// 	portName: string,
// } => {
// 	const sp = childPort.split('_');
// 	if (sp.length < 3) { throw new Error(`Unknown child identifier format [${childPort}]`); }

// 	const widgetId = sp[0];
// 	const portType = sp[1] as PortType;
// 	const portName = sp[2];
// 	return {
// 		portType,
// 		// portIndex,
// 		widgetId,
// 		portName,
// 	};
// };

// /**
//  * Creates an identifier for a widget port which encodes information about the port type and index.
//  * @param indexOrName is the index of the port within the widget or its unique name
//  * @param portType the type of port (input or output)
//  */
// const createWidgetPortIdentifier = (
// 	widgetId: string,
// 	portType: PortType,
// 	name: string,
// ): WidgetPortIdentifierWithName => {
// 	return `${widgetId}_${portType}_${name}`;
// };

export type DecodedGatePortInfo = {
	gateId: string;
	portType: PortType;
	portName?: string;
};



/**
 * Creates a list of unique gate ports by using the entity's id as the port name
 * @param portsNames list of input or output names of the gate
 * @param entityId the id of the gate in the block or the block in the recipe
 * @param recipeId is the id of the recipe in the pool. 
 * NOTE: Recipe Id is an extra added layer of uniqueness to allow the same recipe
 * to be loaded multiple times without conflicts between their ports.
 */
const _getUniqueBlockPorts = (portsNames: string[], entityId: string, recipeId: string): BlockPort[] => {
	const res = portsNames.map(name => ({
		name: name,
		id: `${KEMU_ID_MARK}${recipeId}_${entityId}_${name}`,
	}));

	return res;
};

/**
 * Transforms a list of WidgetPorts into a list of GatePorts with unique ids
 * @param IOs list of ports to transform
 * @param type whether the given IOs are inputs are outputs
 * @param gateId the id of the gate in the block
 * @param blockId the id of the block in the recipe (recipeId property)
 * @param recipeId the id of the recipe in the pool
 */
// const identifyWidgetPorts = (IOs: WidgetPort[], type: 'input' | 'output', gateId: string, blockId: string, recipeId: string): GatePort[] => {
// 	const uniquePorts: GatePort[] = IOs.map((input, index) => {
// 		return {
// 			name: input.name,
// 			id: getUniqueGatePortId(index, type, gateId/*, blockId, recipeId*/)
// 		};
// 	});

// 	return uniquePorts;
// };

const letters = 'abcdefghijklmnopqrstuvwxyz';
const genericAlphabet = `${letters.toUpperCase()}${letters}1234567890`;
const getIdFromAlphabet = customAlphabet(genericAlphabet);

/**
 * Generates a valid id removing any invalid characters
 */
const getRandomId = (len: number): string => {
	// We prevent using underscores because that character is used while defining
	// the ids of some ports.

	// NOTE: given the new custom alphabet (as of 29/Aug/2023), it is impossible to generate an id with '_', however,
	// I'm leaving this here for future reference to emphasize the fact that we cannot use such character
	// and to avoid potential bugs if the custom alphabet is changed.
	let id = getIdFromAlphabet(len).replace(/_/g, '-');
	// Make sure the ID marker is not accidentally generated.
	id = id.replace(KEMU_ID_MARK, '[#]');
	return id;
};

/**
 * Generates a unique name to be used as a port name. 
 * NOTE: this method does NOT guarantee the name is unique within the widget.
 * @param name the base name to be used.
 * @param existing optional list of existing names to check against, this is used to guarantee uniqueness.
 */
const getRandomPortName = (name: string, existing?: string[]): string => {
  const portName = `${name}-${getRandomId(6)}`;
	if (existing && existing.includes(portName)) {
		return getRandomPortName(name, existing);
	}

	return portName;
};

/**
 * Replaces all the IDs in a recipe with new ones.
 * This method assumes all ID types in the given recipe are prepended
 * with the generic marker defined by `KEMU_ID_MARK`.
 * @param recipeStr the recipe to process in stringify format.
 * @returns a copy of `recipeStr` with all the ids replaced.
 */
const regenerateIds = (recipeStr: string): string => {
	const regex = new RegExp(`${KEMU_ID_MARK}[.]*[^"_\\\\]*`, 'gm');
	let m;
	const dictionary: Record<string, string> = {};
	const len = 7;
	while ((m = regex.exec(recipeStr)) !== null) {
		// This is necessary to avoid infinite loops with zero-width matches
		if (m.index === regex.lastIndex) {
			regex.lastIndex++;
		}

		// [0] is full match
		if (!dictionary[m[0]]) {
			dictionary[m[0]] = `${KEMU_ID_MARK}${getRandomId(len)}`;
		}
	}

	// Replace all ids as per dictionary 
	let newStr = recipeStr;
	Object.keys(dictionary).forEach(key => {
		// Find strings that start with double quotes + ID marker ("{#}ID) but without closing quotes,
		// this in order to include the references of children ports which have the new formatting:
		// `{#}WidgetId_type_portName`
		newStr = newStr.split(`"${key}`).join(`"${dictionary[key]}`);
	});

	return newStr;
};

/**
 * Removes the id marker from a string, globally
 * @param str the string to search the ID marker for
 */
const removeIdMarker = (str: string): string => {
	return str.replace(new RegExp(KEMU_ID_MARK, 'gm'), '');
};

/**
 * Returns the string used as an id marker
 */
const getIdMarker = (): string => KEMU_ID_MARK;

/**
 * Replaces all the ids in the given string that contain an ID marker with the newly
 * provided one. 
 * @param str string to replace ids from
 * @param oldId the id to be replaced including the id marker
 * @param newId the new id excluding the id marker.
 * @returns `str` with the replaced ids.
 */
const replaceMarkedIds = (str: string, oldId: string, newId: string) => {
	const regex = new RegExp(`${oldId}`, 'gm');
	return str.replace(regex, `${KEMU_ID_MARK}${newId}`);
};

/**
 * Returns a shallow reference to the block's `gates` property.
 * WARNING: Mutating the inner properties of objects in the returned dictionary WILL
 * mutate the objects in the recipe.
 */
const getWidgetsInThing = (recipeId: string, blockId: string): WidgetsMap => {
	const block = findThingInRecipe(recipeId, blockId);
	if (!block) { throw new Error(`Recipe [${recipeId}] has not been registered`); }
	return { ...block.gates };
};

/**
 * @deprecated use `getWidgetsInThing` instead
 */
const getWidgetsInBlock = getWidgetsInThing;

/**
 * Adds a brand new gate to the given block in the recipe. 
 * NOTE: This method does NOT manipulate the UI
 * @param recipeId the recipe pool id
 * @param thingId the id of the Thing in the recipe the widget will be added to
 * @param type the type of gate to add
 * @param position the position in the canvas of the new widget.
 * @param parentGroupId the id of the group this widget belongs to.
 * @param defaultState the default state of the widget. If not provided, the widget processor's
 * getDefaultState() function (if available) will be used to get the default state.
 * @returns a reference to the newly added gate. WARNING: mutating the returned reference
 * WILL mutate the newly added object
 */
const addWidgetToThing = (config: {
	recipeId: string,
	thingId: string,
	type: WidgetType,
	position: Position,
	parentGroupId?: string,
	defaultState?: WidgetState,
}): RecipeWidget => {
	const { recipeId, thingId, type, position, parentGroupId, defaultState } = config;

	const thing = findThingInRecipe(recipeId, thingId);
	if (!thing) { throw new Error(`Recipe [${recipeId}] has not been registered`); }

	const gateModule = GatesManager.getGateByType(type);
	// const gateDefaultState = gateModule.getDefaultState ? gateModule.getDefaultState() : {};
	// const gateInputs = gateModule.getInputNames && gateModule.getInputNames(gateDefaultState) || [];
	// const gateOutputs = gateModule.getOutputNames && gateModule.getOutputNames(gateDefaultState) || [];
	// Make sure IDs are unique within the block
	const id = generateUniqueWidgetId(thing);

	// NOTE: WE can potentially add the port type (since getInputNames is returning it) to the recipe itself.
	const newGate: RecipeWidget = {
		id,
		canvas: {
			// position: getDefaultWidgetPosition(recipeId, blockId, type, block.gates) 
			position
		},
		children: [],
		// inputPorts: identifyWidgetPorts(gateInputs, 'input', id, blockId, recipeId),
		// outputPorts: identifyWidgetPorts(gateOutputs, 'output', id, blockId, recipeId),
		state: defaultState || (gateModule.getDefaultState ? gateModule.getDefaultState() : {}),
		type: type
	};

	if (parentGroupId) {
		newGate.groupId = parentGroupId;
	}

	thing.gates[id] = newGate;
	return newGate;
};

/**
 * Adds a widget to a Thing preserving a given widget id. Does not initialize the widget.
 * Prefer this utility when reconstructing from snapshots or orchestrated diffs to avoid id churn.
 */
export type AddWidgetWithIdConfig = {
  /** Recipe pool id */
  recipeId: string;
  /** Thing recipe id where the widget will be added */
  thingId: string;
  /** The exact id to assign to the new widget (must be unique within the thing) */
  widgetId: string;
  /** Type of widget to add */
  widgetType: WidgetType;
  /** Canvas position of the widget */
  position: Position;
  /** Optional group container id the widget belongs to */
  groupId?: string;
  /** Optional full widget state; if omitted, uses the widget's default state */
  state?: WidgetState;
};

/**
 * Adds a widget with a fixed id to the given Thing. This function mutates the recipe cache.
 * It does not perform initialization; call `initializeAddedWidgets`/`kemuCore.initializeWidget` after adding.
 */
const addWidgetWithIdToThing = (config: AddWidgetWithIdConfig): RecipeWidget => {
  const { recipeId, thingId, widgetId, widgetType, position, groupId, state } = config;
  const thing = findThingInRecipe(recipeId, thingId);
  if (!thing) { throw new Error(`Recipe [${recipeId}] has not been registered`); }

  if (thing.gates[widgetId]) {
    throw new Error(`Widget id ${widgetId} already exists in thing ${thingId}`);
  }

  const gateModule = GatesManager.getGateByType(widgetType);
  const newWidget: RecipeWidget = {
    id: widgetId,
    canvas: { position },
    children: [],
    state: state ?? (gateModule.getDefaultState ? gateModule.getDefaultState() : {}),
    type: widgetType,
  };

  if (groupId) {
    newWidget.groupId = groupId;
  }

  thing.gates[widgetId] = newWidget;
  return newWidget;
};

/**
 * @deprecated use `addWidgetToThing` instead
 */
const addGateToBlock = addWidgetToThing;

/**
 * Creates a new widget type 'group' and adds it to the given thing
 * @param recipeId the recipe pool id
 * @param thingId the id of the thing (recipeId) the gate will be added to
 * @param position the position of the new widget.
 * @param defaultState the default state of the widget.
 * @param parentGroupId the id of the group this widget belongs to
 * @returns a reference to the newly added gate. WARNING: mutating the returned reference
 * WILL mutate the newly added object
 */
const addCustomWidgetToThing = (
	recipeId: string,
	thingId: string,
	position: Position,
	defaultState: Omit<WidgetGroupState, 'type'>,
	parentGroupId?: string,
): RecipeWidget => {

	const type = WidgetType.widgetGroup;
	const newWidget = addWidgetToThing({
		recipeId,
		thingId,
		type,
		position,
		parentGroupId
	});


	const newState: Partial<WidgetGroupState> = {
		// @ts-expect-error intentional
		canvasPosition: {
			x: 0,
			y: 0,
		},
		...defaultState,
		type: 'custom',
	};


	newWidget.state = newState as WidgetState<WidgetGroupState>;

	return newWidget;
};

let repeatedPasteCount = 0;
let lastPastedWidgetsSignature: string | undefined;
let lastTargetLocation: Position | undefined;

/**
 * Shifts the position of the widgets if they have been pasted multiple times.
 * @param config - The configuration object.
 * @param config.widgetsList - The list of widgets to shift.
 * @param config.targetLocation - The target location of the widgets.
 * @param config.oldWidgetsSignature - The signature of the widgets that were pasted.
 */
const shiftWidgetPosIfNecessary = (config: {
	widgetsList: RecipeWidget[];
	targetLocation: Position;
	oldWidgetsSignature: string;
}): void => {
	const { widgetsList, targetLocation, oldWidgetsSignature } = config;
	if (lastPastedWidgetsSignature === oldWidgetsSignature && lastTargetLocation === targetLocation) {
		repeatedPasteCount++;
		widgetsList.forEach((widget) => {
			widget.canvas.position.x += repeatedPasteCount * 10;
			widget.canvas.position.y += repeatedPasteCount * 10;
		});
	} else {
		lastTargetLocation = targetLocation;
		lastPastedWidgetsSignature = oldWidgetsSignature;
		repeatedPasteCount = 0;
	}
};

/**
 * Adds the given stringify map of widgets into the Thing.
 * This method automatically re-generates ids before adding the widgets to the block.
 * @param mapData WidgetsMap in string format
 * @param recipeId the id of the recipe in the pool
 * @param thingId the id of the thing (recipeId) the widgets will be added to
 * @param targetLocation the relative position widgets will be added from
 * @param groupId id of the group within the Thing that widgets will be added to
 * @returns a map of the newly added widgets. WARNING: Mutating this object will mutate
 * the object in the recipe pool.
 */
const addWidgetsFromStringMap = async (
	mapData: string,
	recipeId: string,
	thingId: string,
	targetLocation: Position,
	zoom: number,
	groupId?: string
): Promise<{
	thingWidgets: WidgetsMap;
	addedWidgets: WidgetsMap;
	/** 
	 * a map that links the original widget id
	 * with the newly generated one.
	 * oldId => newId
	 */
	widgetsIdMap: Record<string, string>;
	originalParsedData: WidgetsMap;
}> => {
	const thing = findThingInRecipe(recipeId, thingId);
	if (!thing) { throw new Error(`Recipe [${recipeId}] has not been registered`); }

	try {
		const originalData = JSON.parse(mapData) as WidgetsMap;
		const dataWithNewIds = regenerateIds(mapData);
		const widgetsMap = JSON.parse(dataWithNewIds) as WidgetsMap;
		const widgetsList = Object.values(widgetsMap);

		/** 
		 * IMPORTANT: here we are building a dictionary of 
		 * oldWidgetId => newWidgetId. This works because
		 * after the ids are regenerated, it is expected the
		 * objects remain in the same location. If this was to change
		 * the following logic would break.
		 */
		const widgetsIdMap: Record<string, string> = {};
		const oldWidgetsList = Object.values(originalData);
		let oldWidgetsSignature = '';

		oldWidgetsList.forEach((oldWidget, index) => {
			widgetsIdMap[oldWidget.id] = widgetsList[index].id;
			// This is a signature of the widgets in the source map. It is used to check if the widgets have been pasted multiple times.
			oldWidgetsSignature += `${oldWidget.id.replace(KEMU_ID_MARK, '')}-`;
		});

		if (widgetsList.length) {
			// Find the least top/left widget in the root (not added to a group), 
			// since all other widgets will be positioned relative to it.
			const leastTopLeftPosition: Position = { x: Infinity, y: Infinity };
			widgetsList.forEach((widget) => {
				if (!widget.groupId) {
					if (widget.canvas.position.x < leastTopLeftPosition.x) {
						leastTopLeftPosition.x = widget.canvas.position.x;
					}

					if (widget.canvas.position.y < leastTopLeftPosition.y) {
						leastTopLeftPosition.y = widget.canvas.position.y;
					}
				}
			});

			// Calculate distance from desired drop location
			const deltaX = (targetLocation.x / zoom) - leastTopLeftPosition.x;
			const deltaY = (targetLocation.y / zoom) - leastTopLeftPosition.y;

			// Update the position of ALL parent widgets
			widgetsList.forEach((widget) => {
				// Only modify the widgets that are NOT within a group
				if (!widget.groupId) {
					widget.canvas.position.x += deltaX;
					widget.canvas.position.y += deltaY;
					// Since this is a root level widget, assign them to the given group (if provided)
					if (groupId) { widget.groupId = groupId; }
				}
			});

			shiftWidgetPosIfNecessary({ widgetsList, targetLocation, oldWidgetsSignature });
		}

		// Add new widgets to the thing
		thing.gates = {
			...thing.gates,
			...widgetsMap
		};

		return {
			thingWidgets: thing.gates,
			addedWidgets: widgetsMap,
			widgetsIdMap,
			originalParsedData: originalData,
		};
	} catch (e) {
		console.warn(`Failed to decode clipboard data. Aborting paste action`);
		return {
			thingWidgets: thing.gates,
			addedWidgets: {},
			widgetsIdMap: {},
			originalParsedData: {},
		};
	}
};

/**
 * Loops through the given widgets and removes children references to widgets that do not exist
 * in the given map.
 * @returns a reference to the updated widgets map. WARNING: Mutating this object will mutate
 * the given widgetMap
 */
const removeOrphanConnections = <T extends Record<string, RecipeWidget> = Record<string, RecipeWidget>>(widgetMap: T): T => {
	const widgets = Object.values(widgetMap);
	widgets.forEach(widget => {
		widget.children = widget.children.filter(child => widgetMap[child.childId]);
	});

	return widgetMap;
};

/**
 * Removes orphan widgets that reference non-existent groups.
 * A widget is considered orphan if it has a groupId that doesn't match any existing group widget.
 * This applies to both regular widgets and group widgets - if a group widget's parent group doesn't exist,
 * it should also be removed.
 * @param widgetMap the map of widgets to filter
 * @returns a new map with orphan widgets removed
 */
const removeOrphanWidgets = <T extends Record<string, RecipeWidget> = Record<string, RecipeWidget>>(widgetMap: T): T => {
	// First, collect all group widget IDs
	const allGroupIds = new Set<string>();
	for (const widgetId in widgetMap) {
		const widget = widgetMap[widgetId];
		if (widget.type === WidgetType.widgetGroup) {
			allGroupIds.add(widgetId);
		}
	}

	// Build set of valid groups by starting from root groups (no parent) and traversing
	const validGroupIds = new Set<string>();
	const visited = new Set<string>();
	
	// Helper to check if a group is valid (recursive, handles circular references)
	const isValidGroup = (groupId: string): boolean => {
		if (visited.has(groupId)) {
			return validGroupIds.has(groupId);
		}
		visited.add(groupId);
		
		const group = widgetMap[groupId];
		// Group must exist and be a widgetGroup type
		if (!group || group.type !== WidgetType.widgetGroup) {
			return false;
		}
		
		// Root group (no parent) or parent is valid group
		const isValid = group.groupId === undefined 
			|| (group.groupId !== groupId && allGroupIds.has(group.groupId) && isValidGroup(group.groupId));
		
		if (isValid) {
			validGroupIds.add(groupId);
		}
		return isValid;
	};
	
	// Identify all valid groups
	for (const groupId of allGroupIds) {
		isValidGroup(groupId);
	}

	// Filter out widgets (including group widgets) that have a groupId that doesn't exist or isn't valid
	const filteredMap = {} as T;
	for (const widgetId in widgetMap) {
		const widget = widgetMap[widgetId];
		
		// If widget has a groupId, verify it exists and is valid
		if (widget.groupId !== undefined) {
			if (!validGroupIds.has(widget.groupId)) {
				// Skip this orphan widget (applies to both regular widgets and group widgets)
				continue;
			}
		}
		
		// Widget is valid (no groupId or has valid groupId)
		filteredMap[widgetId] = widget;
	}

	// Clean up children references to removed widgets
	removeOrphanConnections(filteredMap);

	return filteredMap;
};

/** 
 * Adds a new custom widget into the thing from the given template. This method also creates and adds
 * any inner widget contained by the custom widget.
 * @param template the widget to be added to the thing
 * @param recipeId the id of the recipe in the pool
 * @param thingId the id of the thing (recipeId) the gate will be added to
 * @param parentGroupId the id of the group this widget belongs to
 * @returns maps to the newly added widgets and the current map of widgets in the Thing. WARNING: mutating
 * any object in these maps will mutate the original object in the recipe.
*/
const addCustomWidgetFromTemplate = async (
	template: WidgetCollectionItem,
	recipeId: string,
	thingId: string,
	position?: Position,
	parentGroupId?: string,
	config?: {
		/** if true, all the inner connections of the widgets will be removed */
		removeConnections?: boolean;
	}
): Promise<{
	/** 
	 * the current map of widgets in the Thing. WARNING: this is a direct reference to the recipe, mutating
	 * any object in this map will mutate the original object in the recipe.
	 */
	thingWidgets: WidgetsMap;
	/** 
	 * a map of the newly added widgets. Some objects here and in `thingWidgets` will overlap.
	 * WARNING: mutating the returned reference WILL mutate the newly added object. 
	 */
	addedWidgets: WidgetsMap;
}> => {
	const thing = findThingInRecipe(recipeId, thingId);
	if (!thing) { throw new Error(`Recipe [${recipeId}] has not been registered`); }

	let newWidgetsMap: WidgetsMap = {};

	/* if (template.variant === CustomWidgetVariant.Bundle) {
		const defaultState = getDefaultWidgetBundleState();

		const widgetState: WidgetBundleState = {
			...defaultState,
			// IMPORTANT: Widget Bundle's initialization function is in charge of loading
			// the processor and stored state. Here we MUST set the collection info
			// so it knows where to load the widget from.
			collectionInfo: {
				userId: template.author.id,
				version: template.version,
				widgetId: template.dbId,
			}
		};

		// Make sure IDs are unique within the Thing
		const id = generateUniqueWidgetId(thing);
		// const parentWidget = parentGroupId ? thing.gates[parentGroupId] : null;
		const canvasPosition = position
			? position
			: getDefaultWidgetPosition(recipeId, thingId, WidgetType.widgetBundle, thing.gates);

		const newBundleWidget: RecipeWidget = {
			id,
			canvas: {
				position: canvasPosition
			},
			children: [],
			state: widgetState as CustomWidgetState<WidgetBundleState>,
			type: WidgetType.widgetBundle,
		};

		if (parentGroupId) {
			newBundleWidget.groupId = parentGroupId;
		}

		thing.gates[id] = newBundleWidget;
		newWidgetsMap[id] = newBundleWidget;

		// Finally we MUST initialize the widget so it can load the processor and state
		await kemuCore.initializeWidget(recipeId, thingId, id);
	} else  */if (template.variant === CustomWidgetVariant.Group) {
		if (!template.contents) { throw new Error(`Missing contents, this method cannot process widget bundles.`); }
		// Parse the widget contents with new IDS (to avoid collisions if adding the same widget multiple times)
		const widgetsMap: ParsedWidgetContents = JSON.parse(regenerateIds(template.contents));

		// Remove any orphan connections that might have been saved in the template
		removeOrphanConnections(widgetsMap);

		// Find the container widget again (since the ids changed after regeneration)
		const widgetParent = Object.values(widgetsMap).find(widget => widget.isContainer === true);
		if (!widgetParent) { throw new Error(`Containing widget not found in contents`); }

		const widgetState = widgetParent.state as CustomWidgetState<WidgetGroupState>;

		if (config?.removeConnections) {
			logger.log('Removing inner connections from widget. Original map: ', jsonCloneObject(widgetsMap));
			Object.values(widgetsMap).forEach(widget => {
				widget.children = [];
			});

			// Also set the new protocol of the widget now that the connections have been removed
			widgetState.protocolVersion = '2.0';
		}

		// Restore the link with the collection itself.
		widgetState.collectionInfo = {
			userId: template.author.id,
			version: template.version,
			widgetId: template.dbId,
		};

		// Assign container group 
		widgetParent.groupId = parentGroupId;
		// Change the default position of parent
		widgetParent.canvas = {
			position: position ? position : getDefaultWidgetPosition(recipeId, thingId, widgetParent.type, thing.gates)
		};

		// Add new list of gates to thing
		thing.gates = {
			...thing.gates,
			...widgetsMap,
		};

		// Create a map of the new widgets
		newWidgetsMap = {
			...newWidgetsMap,
			...widgetsMap
		};

		// // Find all bundles with a collection info and initialize them
		// const bundles = Object.values(widgetsMap).filter(widget => widget.type === WidgetType.widgetBundle);
		// for (const bundle of bundles) {
		// 	const bundleState: WidgetBundleState = bundle.state as CustomWidgetState<WidgetBundleState>;
		// 	if (bundleState.collectionInfo) {
		// 		await kemuCore.initializeWidget(recipeId, thingId, bundle.id);
		// 	}
		// }
	} else {
		console.error(`Unknown widget variant [${template.variant}], cannot add widget to thing`);
	}



	return {
		addedWidgets: newWidgetsMap,
		thingWidgets: thing.gates,
	};
};

/**
 * Replaces the internal state of a widgetGroup Type
 * @param recipeId the recipe pool id
 * @param thingId the id of the thing (recipeId) the gate will be added to
 * @param widgetId the id of the widget to change
 * @param state the new state of the widget
 * @param triggerEvents whether event listeners should be notified. Defaults to true.
 * @returns a reference to the updated widget. WARNING updating this widget properties WILL mutate the original object in the recipe
 */
const editCustomWidgetState = (recipeId: string, thingId: string, widgetId: string, state: WidgetState<WidgetGroupState>, triggerEvents=true): RecipeWidget => {
	const widget = findWidgetInRecipe(recipeId, thingId, widgetId);
	if (!widget) { throw new Error(`Widget ${widgetId} not found in thing ${thingId} and recipe ${recipeId}`); }
	if (widget.type !== WidgetType.widgetGroup) { throw new Error(`The given widget id is not a widgetGroup type`); }
	// Change the state in the recipe
	kemuCore.setGateState(recipeId, thingId, widgetId, state, triggerEvents);
	// At this point the reference should have been updated
	return widget;
};

/**
 * Reads the current state of the custom widget.
 * @param recipeId the recipe pool id.
 * @param thingId the id of the thing (recipeId) the gate will be added to.
 * @param widgetId the id of the custom widget.
 * @returns a deep COPY of the widget state. 
 */
const getCustomWidgetState = (recipeId: string, thingId: string, widgetId: string): WidgetGroupState => {
	const widget = findWidgetInRecipe(recipeId, thingId, widgetId);
	if (!widget) { throw new Error(`Widget ${widgetId} not found in thing ${thingId} and recipe ${recipeId}`); }
	if (widget.type !== WidgetType.widgetGroup) { throw new Error(`The given widget id is not a widgetGroup type`); }
	return JSON.parse(JSON.stringify(widget.state));
};

/**
 * Resets all custom field values in a widget group by setting customValue to defaultValue.
 * This causes fields to revert to their defaultValue and ensures proper change detection.
 * @param recipeId the recipe pool id
 * @param thingId the id of the thing (recipeId) the widget belongs to
 * @param widgetId the id of the custom widget
 * @returns the updated widget state
 */
const resetWidgetGroupFields = (recipeId: string, thingId: string, widgetId: string): WidgetGroupState => {
	const currentState = getCustomWidgetState(recipeId, thingId, widgetId);
	if (!currentState || !currentState.settings) {
		return currentState;
	}

	// Reset customValue to defaultValue for all field configs
	const updatedSettings = currentState.settings.map((setting) => {
		if (setting.config && typeof setting.config === 'object') {
			const config = setting.config as DynamicFieldBaseConfig;
			const hasDefaultValue = 'defaultValue' in config && config.defaultValue !== undefined;
			const hasCustomValue = 'customValue' in config;

			// If defaultValue exists, set customValue to defaultValue; otherwise remove customValue
			if (hasDefaultValue) {
				return {
					...setting,
					config: {
						...config,
						customValue: config.defaultValue,
					},
				};
			} else if (hasCustomValue) {
				// Remove customValue if no defaultValue exists
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				const { customValue: _, ...restConfig } = config;
				return {
					...setting,
					config: restConfig,
				};
			}
		}
		// Always return a new object to ensure proper state updates
		return {
			...setting,
			config: setting.config ? { ...(setting.config as DynamicFieldBaseConfig) } : setting.config,
		};
	});

	const newState = {
		...currentState,
		settings: updatedSettings,
	} as WidgetState<WidgetGroupState>;

	// Update widget state
	editCustomWidgetState(
		recipeId,
		thingId,
		widgetId,
		newState,
		true
	);

	// Update variable manager for each setting to ensure UI components reflect the reset
	// This is necessary because field components check the variable manager first before syncing from config
	for (const setting of updatedSettings) {
		if (setting.config && typeof setting.config === 'object') {
			const config = setting.config as DynamicFieldBaseConfig;
			const valueToSet = ('customValue' in config ? config.customValue : undefined) ?? config.defaultValue;

			if (valueToSet !== undefined && valueToSet !== null) {
				variablesManager.setVariableValue({
					variableName: getControlScopedVarName(setting.variableName),
					setByWidgetId: widgetId,
					value: valueToSet as SupportedTypes,
					type: decodeType(valueToSet),
				});
			}
		}
	}

	return {
		...currentState,
		settings: updatedSettings,
	};
};

/**
 * Returns a map of all inner widgets in the given group (aka custom widget) including the widget group itself.
 * @param customWidgetId the id of the group (widgetGroup) that contains the other widgets to look for.
 * @param thingId the id of the thing (recipeId) in the recipe
 * @param recipeId the id of the recipe in the pool
 * @param idsInSelection a list of widgets id that should be preserved if referenced as children
 * @returns a deep copy of the widgets. Changing this object has no effect in the recipe state.
 */
const getCustomWidgetInnerWidgets = (
	customWidgetId: string,
	thingId: string,
	recipeId: string,
	idsInSelection: string[] = [],
): WidgetsMap => {
	const thing = findBlockInRecipe(recipeId, thingId);
	if (!thing) { throw new Error(`Thing ${thingId} not found in recipe ${recipeId}`); }

	const widget = safeJsonClone<RecipeWidget>(thing.gates[customWidgetId]);
	if (!widget) { throw new Error(`Widget ${customWidgetId} not found in thing ${thingId} and recipe ${recipeId}`); }

	if (customWidgetId.includes('_')) { throw new Error(`Widget IDs cannot contain underscore (_). Provided id "${customWidgetId}"`); }

	// Remove references to collections, this prevents copies from being able to modify
	// the original collection.
	if (widget.type === WidgetType.widgetGroup && widget.state) {
		// IMPORTANT: This is safe to do so here only because `widget` is a copy
		const widgetState = widget.state as CustomWidgetState<WidgetGroupState>;
		widgetState.collectionInfo = undefined;
	}

	// Get parents connected to inner inputs
	// const parentsInfo = getWidgetParents(customWidgetId, thingId, recipeId);
	// Build a list of related widgets (group, parents and children)
	let gatesMap: WidgetsMap = {};
	gatesMap[widget.id] = widget;

	// Remove any reference to attached children not inside of the widget
	widget.children = widget.children.filter((child) => {
		// This might be undefined if the group has lost references of children
		// that no longer exist.
		const childInfo = thing.gates[child.childId] as RecipeWidget | undefined;
		const decodedSourcePort = decodeWidgetPortIdentifier(child.sourcePort);
		const decodedTargetPort = decodeWidgetPortIdentifier(child.targetPort);
		return (
			// Keep if child is linked to an inner output (group's inputs)
			// child.sourcePort.startsWith('innerOutput')
			decodedSourcePort.portType === 'innerOutput'
			// Keep if child is linked to an inner input (group's outputs)
			// || child.targetPort.startsWith('innerInput')
			|| decodedTargetPort.portType === 'innerInput'
			// Keep if the target's group id is the same as the widget id
			// NOTE: this would also include widgets that are at the root level
			// where both childInfo.groupId is undefined. The caller needs
			// to remove root level widgets that are not part of the selection.
			|| (childInfo && childInfo.groupId === widget.groupId)
			|| idsInSelection.includes(child.childId)
		);
	});

	// Look for all the gates (widgets) that belong to the custom widgetId 
	for (const gateId in thing.gates) {
		const child = thing.gates[gateId];
		if (child.groupId === customWidgetId) {
			// If a group itself, include its inner children
			if (child.type === WidgetType.widgetGroup) {
				// NOTE: this returns the group itself as well
				const nestedChildren = getCustomWidgetInnerWidgets(child.id, thingId, recipeId);
				gatesMap = {
					...gatesMap,
					...nestedChildren
				};
			} else {
				// Save a copy of the widget
				gatesMap[gateId] = safeJsonClone<RecipeWidget>(child);

				// Update: Commenting out below code since the `collectionInfo` is no longer private and
				// should persist in the the above copy.

				// We must keep widget bundle's collection information, otherwise we won't be able to load the widget's
				// processor when the group is added to the canvas. Here we restore the collection info.
				// if (child.type === WidgetType.widgetBundle && thing.gates[gateId].state) {
				// 	const originalChildState: WidgetBundleState = child.state as CustomWidgetState<WidgetBundleState>;
				// 	// Saved child state
				// 	const savedChild = gatesMap[gateId];
				// 	if (originalChildState.collectionInfo) {
				// 		savedChild.state = {
				// 			...savedChild.state,
				// 			collectionInfo: jsonCloneObject<CustomWidgetState<WidgetBundleState>>(originalChildState.collectionInfo),
				// 		};
				// 	}
				// }
			}
		}
	}

	return gatesMap;
};

/**
 * Finds the DOM element that represents the given entity
 * @param recipeId the id of the recipe in the pool
 * @param blockId the id of the block in the recipe (recipeId property)
 * @param entityId Optional. the id of the entity inside of the Thing to find in the doom 
 */
const getEntityDomEl = (recipeId: string, blockId: string, entityId?: string): HTMLElement | null => {
	const domId = generateDomId(recipeId, blockId, entityId);
	const domEl = document.getElementById(domId);
	if (domEl) { return domEl; }
	return null;
};



/**
 * Changes the state of the widget. Disabled widgets are not invoked by the Kemu processor.
 * @param recipeId the id of the recipe in the pool
 * @param blockRecipeId the id of the block in the recipe (recipeId property)
 * @param widgetId the id of the widget inside the block to be disabled
 * @param disable new state of the widget
 * @returns a reference to the updated widget. IMPORTANT: modifying the returned reference
 * WILL modify the stored object.
 */
const disableWidget = (recipeId: string, blockRecipeId: string, widgetId: string, disable: boolean): RecipeWidget => {
	const targetBlock = findBlockInRecipe(recipeId, blockRecipeId);
	if (!targetBlock) { throw new Error(`Block ${blockRecipeId} does not exist in recipe ${recipeId}`); }
	const widget = targetBlock.gates[widgetId];
	if (!widget) { throw new Error(`Gate ${widgetId} not found in block ${blockRecipeId}`); }

	widget.disabled = disable;
	return widget;
};


/**
 * Non-reactive method to return the disabled state of a widget.
 * @param recipeId the id of the recipe in the pool
 * @param blockRecipeId the id of the block in the recipe (recipeId property)
 * @param widgetId the id of the widget inside the block to be disabled
 * @returns true if the widget is disabled
 */
const getWidgetDisabled = (recipeId: string, blockRecipeId: string, widgetId: string): boolean => {
	const targetBlock = findBlockInRecipe(recipeId, blockRecipeId);
	if (!targetBlock) { throw new Error(`Block ${blockRecipeId} does not exist in recipe ${recipeId}`); }
	const widget = targetBlock.gates[widgetId];
	if (!widget) { throw new Error(`Gate ${widgetId} not found in block ${blockRecipeId}`); }

	return !!widget.disabled;
};

/***
 * Clones an existing gate and adds it to the same target block.
 * @param recipeId the id of the recipe in the pool
 * @param blockRecipeId the id of the block in the recipe (recipeId property)
 * @param sourceGateId the id of the gate inside the block to be cloned
 * @returns a reference to the newly added gate. IMPORTANT: modifying the returned reference
 * WILL modify the stored object.
 */
// const duplicateGate = (recipeId: string, blockRecipeId: string, sourceGateId: string): RecipeWidget => {
// 	const targetBlock = findBlockInRecipe(recipeId, blockRecipeId);
// 	if (!targetBlock) { throw new Error(`Block ${blockRecipeId} does not exist in recipe ${recipeId}`); }
// 	const sourceGate = targetBlock.gates[sourceGateId];
// 	if (!sourceGate) { throw new Error(`Gate ${sourceGateId} not found in block ${blockRecipeId}`); }
// 	// Clone everything, specially the 'state'
// 	const clonedGate: RecipeWidget = JSON.parse(JSON.stringify(sourceGate));
// 	const newGateId = generateUniqueGateId(targetBlock);

// 	clonedGate.id = newGateId;
// 	clonedGate.canvas.position = _getClonedGatePosition(recipeId, blockRecipeId, sourceGate);
// 	clonedGate.children = [];

// 	targetBlock.gates[newGateId] = clonedGate;
// 	// We need to fix all ports
// 	return clonedGate;
// };


/**
 * Options for widget removal operations.
 */
export type RemoveWidgetOptions = {
	/** If true, skip calling the terminate function on the widget */
	skipTerminate?: boolean;
};

/**
 * Removes all the widgets that belong to the given group. This method does NOT remove the 
 * group itself.
 * @param recipePoolId the id of the recipe in the pool
 * @param block an instance of the block in the recipe. This element is NOT modified.
 * @param groupId the id of the group to search for.
 * @param options optional configuration for the removal operation.
 * @returns an updated list of all the remaining widgets in the block.
 */
const removeWidgetsInsideGroup = async (
	recipePoolId: string,
	block: Readonly<RecipeBlock>,
	groupId: string,
	options?: RemoveWidgetOptions
): Promise<WidgetsMap> => {
	const filteredMap: WidgetsMap = {};

	/**
	 * Traverses the parent three starting at the given widget, checking
	 * if the widget is a nested child of the given group.
	 * @param widgetId 
	 */
	const isNestedWidget = (widgetId: string): boolean => {
		const widget = block.gates[widgetId];
		if (!widget) { return false; }
		if (widget.groupId === groupId) { return true; }
		if (widget.groupId) { return isNestedWidget(widget.groupId); }
		return false;
	};

	for (const widgetId in block.gates) {
		const thisWidget = block.gates[widgetId];
		const isInnerWidget = isNestedWidget(widgetId);
		// Create a map of widgets NOT inside of the given group (to be preserved)
		if (!isInnerWidget || widgetId === groupId) {
			filteredMap[widgetId] = thisWidget;
		} else {
			// Widget is inside of the given group (to be removed), so we should terminate it.
			if (!options?.skipTerminate) {
				await kemuCore.terminateWidget(recipePoolId, block.id, thisWidget.id);
			}
		}
	}

	return filteredMap;
};

/**
 * Removes a Widget from a Thing, invoking its termination function if necessary.
 * If the given widget is a group, it also terminates and removes all the widgets inside it.
 * @param recipeId the id of the recipe in the pool
 * @param thingRecipeId the id of the thing in the recipe (recipeId property)
 * @param gateId the id of the gate inside the block to be cloned
 * @param options optional configuration for the removal operation.
 * @returns a reference to the list of gates remaining in the block and their children. IMPORTANT:
 * mutating the returned reference WILL mutate the original object
 */
const removeWidget = async (
	recipeId: string,
	thingRecipeId: string,
	gateId: string,
	options?: RemoveWidgetOptions
): Promise<WidgetsMap> => {
	const targetThing = findThingInRecipe(recipeId, thingRecipeId);
	if (!targetThing) { throw new Error(`Block ${thingRecipeId} does not exist in recipe ${recipeId}`); }
	if (!targetThing.gates[gateId]) { throw new Error(`Gate ${gateId} not found in block ${thingRecipeId}`); }

	let blockGates = targetThing.gates;
	// If a group, we remove any gate that belonged to the given group
	const myself = targetThing.gates[gateId];
	if (myself.type === WidgetType.widgetGroup) {
		blockGates = await removeWidgetsInsideGroup(recipeId, targetThing, gateId, options);
		// Replace the original list of gates in the recipe
		targetThing.gates = blockGates;
	} else {
		// Terminate this widget (unless skipTerminate is set)
		if (!options?.skipTerminate) {
			try {
				await kemuCore.terminateWidget(recipeId, thingRecipeId, gateId);
			} catch (e) {
				if (e?.errCode !== RemoveInvokeError.FunctionNotFound) {
					console.error(`Failed to gracefully terminate widget "${gateId}" in recipe "${recipeId}"`);
				}
			}
		}
	}

	// Remove the gate itself
	delete targetThing.gates[gateId];

	// Now we need to search through all the gates in the block for gates that had the deleted gate
	// as a child
	for (const gate in targetThing.gates) {
		for (let i = targetThing.gates[gate].children.length-1; i >= 0; i--) {
			const childInfo = targetThing.gates[gate].children[i];
			if (childInfo.childId === gateId) {
				targetThing.gates[gate].children.splice(i, 1);
			}
		}
	}

	return targetThing.gates;
};

/**
 * @deprecated use `removeWidget` instead
 */
const removeGate = removeWidget;

/**
 * Replaces the current recipe settings with the given info
 * @param recipeId id of the recipe in the pool
 * @param info information to store
 */
const setCanvasInfo = (recipeId: string, info: RecipeCanvasInfo): void => {
	const recipe = findRecipe(recipeId);
	if (recipe && info) {
		recipe.canvas = JSON.parse(JSON.stringify(info));
	}
};

/**
 * Returns a copy of the recipe's canvas settings
 * @param recipeId id of the recipe in the pool
 * @param info information to store
 * @throws an error if the recipe has not been registered
 */
const getCanvasInfo = (recipeId: string): RecipeCanvasInfo => {
	const recipe = findRecipe(recipeId);
	if (!recipe) { throw new Error(`Recipe [${recipeId}] has not been registered`); }

	return JSON.parse(JSON.stringify(recipe.canvas || {}));
};

/**
 * Replaces the current block's canvas settings with the given info
 * @param recipeId the recipe pool id
 * @param blockRecipeId the id of the block in the recipe (recipeId)
 * @param info information to store
 */
const setBlockCanvasInfo = (recipeId: string, blockRecipeId: string, info: BlockCanvasInfo): void => {
	const block = findBlockInRecipe(recipeId, blockRecipeId);
	if (block && info) {
		block.canvas = JSON.parse(JSON.stringify(info));
	}
};

/**
 * Returns a copy of the block's canvas property.
 * @param recipeId the recipe pool id
 * @param blockRecipeId the id of the block in the recipe (recipeId)
 * @throws an error if the block is not found
 */
const getBlockCanvasInfo = (recipeId: string, blockRecipeId: string): BlockCanvasInfo => {
	const block = findBlockInRecipe(recipeId, blockRecipeId);
	if (!block) { throw new Error(`Block [${blockRecipeId}] does not exist in recipe [${recipeId}]`); }
	return JSON.parse(JSON.stringify(block.canvas || {}));
};


/**
 * Replaces the gate's canvas settings with the given info
 * @param recipeId the recipe pool id
 * @param blockRecipeId the id of the block in the recipe (recipeId)
 * @param gateId the id of the gate in the recipe
 * @param info information to store
 */
const setGateCanvasInfo = (recipeId: string, blockRecipeId: string, gateId: string, info: GateCanvasInfo): void => {
	const gate = findWidgetInRecipe(recipeId, blockRecipeId, gateId);
	if (gate && info) {
		gate.canvas = JSON.parse(JSON.stringify(info));
	}
};


/**
 * Returns a copy of the gate's canvas property or null if the gate was not found
 * @param recipeId the recipe pool id
 * @param thingId the id of the thing in the recipe (recipeId)
 * @param widgetId the id of the gate in the recipe
 * @throws an error if the gate was not found in the recipe
 */
const getWidgetCanvasInfo = (recipeId: string, thingId: string, widgetId: string): GateCanvasInfo => {
	const gate = findWidgetInRecipe(recipeId, thingId, widgetId);
	if (!gate) { throw new Error(`Widget ${widgetId} not found in Thing ${thingId} and recipe ${recipeId}`); }
	return JSON.parse(JSON.stringify(gate.canvas || {}));
};

/**
 * @deprecated use `getWidgetCanvasInfo` instead
 */
const getGateCanvasInfo = getWidgetCanvasInfo;

/**
 * Loops through all the widget in the recipe and generates a zip file for each widget bundle.
 * and adds it to the recipe's storage. It also updates the bundle's state
 * to point to the new storage location.
 * @param recipeId the id of the recipe in the pool
 * @ deprecated widget bundles are no longer supported
 */
// const addWidgetBundlesIntoRecipeStorage = async (recipeId: string): Promise<void> => {
// 	const recipe = findRecipe(recipeId);
// 	if (!recipe) { throw new Error(`Recipe [${recipeId} does not exist]`); }

// 	const recipeThings = recipe.blocks;
// 	for (const thingId in recipeThings) {
// 		const thingWidgets = recipeThings[thingId].gates;

// 		for (const widgetId in thingWidgets) {
// 			const widget = thingWidgets[widgetId];
// 			// If the widget is a bundle, recreate the zip bundle from cache
// 			// and add it to the recipe storage.
// 			if (widget.type === WidgetType.widgetBundle) {
// 				const bundleState = widget.state as CustomWidgetState<WidgetBundleState>;
// 				if (bundleState.$$cacheInfo) {
// 					const isTempBundle = bundleState.$$cacheInfo?.widgetThingId
// 							!== bundleState.collectionInfo?.widgetId;

// 					const { zipHandler } = await widgetBundleManager.recreateBundleFromStorage(
// 						bundleState.$$cacheInfo.widgetThingId,
// 						bundleState.$$cacheInfo.version,
// 						isTempBundle,
// 						{
// 							// IMPORTANT: When the zip file is generated, even though the files are the same, their
// 							// creation date is not. This causes the final checksum to change every time
// 							// the recipe is saved. Here, we force all files to have the same creation date.
// 							// This will ensure the checksum is always the same when the zip contents doesn't change
// 							filesCreationDate: new Date('2022-09-27T10:49:00.000Z').getTime(),
// 						}
// 					);


// 					const zipFile = await widgetBundleManager.generateZipFromHandler(zipHandler);
// 					// IMPORTANT: we pass the current storageUnit in case there was one already created
// 					// in which case the contents would get replaced.
// 					const addedKey = addToStorageUnit(recipeId, thingId, zipFile, bundleState.storageUnitId);
// 					// Update the bundle's state to point to the new storage location
// 					bundleState.storageUnitId = addedKey;
// 				}
// 			}
// 		}
// 	}
// };

/**
 * Finds the recipe in the pool and returns a copy 
 * ready to be stored. Note this method does NOT populate the 'contents' property.
 * @param recipePoolId the id of the recipe in the pool
 * @param name if provided, it will override the existing recipe name.
 * Note that this is a replacement of the returned object, not the original
 * recipe name.
 */
const prepareRecipeForSaving = async <T extends Recipe['author'] | undefined = undefined>(config: {
	recipePoolId: string;
	name?: string;
	createNewUuid?: boolean;
	authorInfo?: T;
	/** Whether to include global variable values in the saved recipe. Defaults to true. */
	saveGlobalVariableValues?: boolean;
}): Promise<{
	parsedContents: T extends Recipe['author'] ? Recipe : Omit<OriginLessRecipe, 'storage'>;
	contents: string;
	/** The original author info in the recipe */
	author: Recipe['author'];
	dbInfo: CachedRecipeDbInfo;
}> => {
	const { recipePoolId, name, createNewUuid, authorInfo, saveGlobalVariableValues = true } = config;
	const recipe = findRecipe(recipePoolId);
	if (!recipe) { throw new Error(`Recipe [${recipePoolId} does not exist]`); }

	// Generate or update storage units for each widget bundle

	// IMPORTANT: remove the the storage BEFORE calling removePrivateProperties.
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { storage, globalVariables: _, dbInfo, author, ...storageLessRecipe } = recipe;

	const cleanRecipe = removePrivateProperties(storageLessRecipe);
	const recipeCopy = JSON.parse(JSON.stringify(cleanRecipe)) as T extends Recipe['author'] ? Recipe : Omit<OriginLessRecipe, 'storage'>;
	if (name) { recipeCopy.name = name; }
	if (createNewUuid) { recipeCopy.uuid = createRecipeUuid(dbInfo.authorId); }

	// Add the given author info if provided
	if (authorInfo) { (recipeCopy as Recipe).author = authorInfo; }

	// Remove all private properties from the blocks state. 
	// A `private property` is any property in the 'state' object that
	// starts with '$$'.
	for (const key in recipeCopy.blocks) {
		const block: RecipeBlock = recipeCopy.blocks[key];
		block.state = removePrivateProperties(block.state);

		// When a block is in dev mode, the 'bundle' property is added during
		// startup, to force the system load the bundle from localhost. 
		// This creates a temporal inconsistency with the types definition.
		// here, we make sure this property is NOT saved as part of the recipe.
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		delete (block as any).bundle;

		// Do the same for all the widget states
		for (const gateKey in block.gates) {
			const gate = block.gates[gateKey];
			gate.state = removePrivateProperties(gate.state);
		}

		// Remove orphan widgets (widgets with groupId that reference non-existent groups)
		block.gates = removeOrphanWidgets(block.gates);
	}



	// Serialize global variables
	const globalVariables = globalVariablesManager.getAllVariables();
	let serializedGlobalVariables;
	if (saveGlobalVariableValues) {
		serializedGlobalVariables = serializeGlobalVariables(globalVariables);
	} else {
		// Strip values - only keep variable definitions
		serializedGlobalVariables = globalVariables.map((variable) => ({
			...variable,
			lastValue: null,
			lastChangedByWidgetId: null,
		}));
	}

	// Set global variables in the recipe
	recipeCopy.globalVariables = serializedGlobalVariables;

	// Raw contents
	const contents = JSON.stringify(recipeCopy);

	return {
		contents,
		author,
		dbInfo,
		parsedContents: recipeCopy,
	};
};

/**
 * Updates a set of limited fields in the cached recipe. 
 * Not provided fields will be ignored.
 * @param recipePoolId the id of the recipe in the pool
 * @param meta the fields to modify
 */
const updateRecipeCacheMeta = (config: {
	recipePoolId: string;
	meta: Partial<RecipeMetaFields>;
}): void => {
	const { recipePoolId, meta } = config;
	const recipe = findRecipe(recipePoolId);
	if (!recipe) { throw new Error(`Recipe [${recipePoolId} does not exist]`); }

	recipe.dbInfo = { ...recipe.dbInfo };

	if (meta.name) {
		recipe.name = meta.name;
	}

	if (meta.recipeType) {
		recipe.dbInfo.recipeType = meta.recipeType;
	}

	if (meta.authorId) {
		recipe.dbInfo.authorId = meta.authorId;
	}

	if (meta.dbId) {
		recipe.dbInfo.id = meta.dbId;
	}

	if (meta.version) {
		recipe.dbInfo.version = meta.version;
	}
};

/**
 * Returns a reference to storage map of a recipe if exists or null if the recipe has no storage.
 * WARNING: Modifying the reference WILL modify the original data.
 * @param recipePoolId the id of the recipe in the pool
 */
const getRecipeStorage = (recipePoolId: string): RecipeStorage | null => {
	const recipe = findRecipe(recipePoolId);
	if (!recipe) { throw new Error(`Recipe [${recipePoolId} does not exist]`); }
	return recipe.storage || null;
};

/**
 * Returns a list of ids representing the unique identifiers of the storage units the
 * block has access to.
 * @param recipeId the id of the recipe in the pool
 * @param blockId The id of the block in the recipe
 */
const getBlockStorageKeys = (recipeId: string, blockId: string): string[] => {
	const block = findBlockInRecipe(recipeId, blockId);
	if (!block) { throw new Error(`Block ${blockId} does not exist in recipe ${recipeId}`); }
	return [...(block.storageUnits || [])];
};

/**
 * Clones the storage unit data and adds it in the recipe's storage. It also adds the new
 * storage unit key to the thing's storageUnits list.
 * @param recipeId the id of the recipe in the pool
 * @param thingRecipeId the id of the thing in the recipe
 * @param key the storage unit key to clone
 * @param newCopyKey the new key to be used for the cloned storage unit.
 * @param defaultData if provided, it will be used in case the storage unit does not exist. 
 * If not provided, this method will throw an error if the storage unit does not exist.
 * @returns the cloned storage unit key
 */
const cloneStorageUnit = (
	recipeId: string,
	thingRecipeId: string,
	key: string,
	newCopyKey: string,
	defaultData?: Uint8Array
): Uint8Array => {
	const recipe = findRecipe(recipeId);
	if (!recipe) { throw new Error(`Recipe [${recipeId} does not exist]`); }
	const thing = findThingInRecipe(recipeId, thingRecipeId);
	if (!thing) { throw new Error(`Thing ${thingRecipeId} does not exist in recipe ${recipeId}`); }
	if (!recipe.storage) { throw new Error(`Recipe [${recipeId}] has no storage`); }

	const storageUnit = recipe.storage[key];
	if (!storageUnit && !defaultData) { throw new Error(`Storage unit [${key}] does not exist in recipe [${recipeId}]`); }

	const unitClone = new Uint8Array(storageUnit || defaultData);

	// Add the new storage unit to the recipe
	recipe.storage[newCopyKey] = unitClone;
	// Add the new storage unit to the block
	thing.storageUnits = [...(thing.storageUnits || []), newCopyKey];
	return unitClone;
};

// /**
//  * Creates a new storage unit at the recipe level and adds the given data.
//  * @param recipePoolId the id of the recipe in the pool
//  * @param blockId The id of the block in the recipe
//  * @param data raw data to store
//  * @param key if provided, it will attempt to override an existing key, granted the block has access to it
//  * @returns the unique identifier for the storage unit that needs to be used to retrieve the contents
//  * back from storage. If key is provided and the operation succeeded, it returns `key`
//  */
// const addToStorage = (recipeId: string, blockId: string, data: Uint8Array, key?: string): string => {
// 	const recipe = findRecipe(recipeId);
// 	if (!recipe) { throw new Error(`Recipe [${recipeId} does not exist]`); }

// 	const block = findBlockInRecipe(recipeId, blockId);
// 	if (!block) { throw new Error(`Block ${blockId} does not exist in recipe ${recipeId}`); }

// 	if (!recipe.storage) { recipe.storage = {}; }
// 	// Make sure the block has storage units
// 	block.storageUnits = block.storageUnits || [];

// 	const unitId = nanoid(4);
// 	let unitKey = `${KEMU_ID_MARK}${unitId}`;

// 	// If key was provided and there were storage units
// 	if (key && block.storageUnits.length) {
// 		// Check if the block is the owner of the requested unit (key)
// 		const blockHasAccess = block.storageUnits.includes(key);
// 		if (!blockHasAccess) { throw new Error(`Block ${blockId} has no access to storage unit ${key}`); }
// 		// Ok, the key does exist and the block is the owner, allow replacement
// 		unitKey = key;
// 	}



// 	// Add/replace the raw data
// 	recipe.storage[unitKey] = data;
// 	// Associate new storage unit to block
// 	const keyExists = block.storageUnits.find(blockKey => blockKey === unitKey);
// 	if (!keyExists) { block.storageUnits.push(unitKey); }

// 	return unitKey;
// };

// /**
//  * Retrieves the stored contents of the given storage unit key.
//  * @param recipeId the id of the recipe in the pool
//  * @param blockId the id of the block in the recipe
//  * @param key the storage unit unique identifier generated by `addToStorage()`.
//  */
// const getStorageUnitFromKey = (recipeId: string, blockId: string, key: string): Uint8Array | null => {
// 	const recipe = findRecipe(recipeId);
// 	if (!recipe) { throw new Error(`Recipe [${recipeId} does not exist]`); }
// 	if (!recipe.storage) { return null; }

// 	// Make sure the block is the owner of the given storage
// 	const block = findBlockInRecipe(recipeId, blockId);
// 	if (!block) { throw new Error(`Block ${blockId} does not exist in recipe ${recipeId}`); }
// 	const blockUnits = block.storageUnits || [];
// 	const hasOwnership = blockUnits.includes(key);
// 	if (!hasOwnership) { throw new Error(`Block ${blockId} has no access to storage unit ${key}`) ; }

// 	return recipe.storage[key] || null;
// };

// /**
//  * Removes an existing unit from storage and the storage key reference from the block.
//  * @param recipeId the id of the recipe in the pool
//  * @param blockId the id of the block in the recipe
//  * @param key the storage unit unique identifier generated by `addToStorage()`.
//  * @returns true if the unit was successfully removed or false if not found.
//  * @throws an error if the block has not access to the storage unit
//  */
// const removeStorageUnit = (recipeId: string, blockId: string, key: string): boolean => {
// 	const recipe = findRecipe(recipeId);
// 	if (!recipe) { throw new Error(`Recipe [${recipeId} does not exist]`); }
// 	// the recipe has no storage
// 	if (!recipe.storage) { return false; }

// 	// The storage unit does not exist
// 	if (!recipe.storage[key]) { return false; }

// 	const block = findBlockInRecipe(recipeId, blockId);
// 	if (!block) { throw new Error(`Block ${blockId} does not exist in recipe ${recipeId}`); }
// 	const blockKeyIndex = (block.storageUnits || []).indexOf(key);
// 	const blockHasAccess = blockKeyIndex !== -1;
// 	// The block has no access to the unit
// 	if (!blockHasAccess) { throw new Error(`Block ${blockId} has no access to storage unit ${key}`); }
// 	delete recipe.storage[key];
// 	// Remove the reference from the block itself
// 	(block.storageUnits || []).splice(blockKeyIndex, 1);
// 	return true;
// };


/**
 * Invokes the block's module to get a list of available actions.
 * @param recipeId the id of the recipe in the pool
 * @param blockId the id of the block in the recipe
 */
// const getBlockActions = (recipeId: string, blockId: string): ReadonlyArray<BlockAction> => {
// 	const block = findBlockInRecipe(recipeId, blockId);
// 	if (!block) { throw new Error(`Block ${blockId} does not exist in recipe ${recipeId}`); }

// 	const blockModule = BlocksManager.getThingWebProcessor(block.id, block.version);
// 	const actions = blockModule.getActions(Object.freeze({ ...block.state }));
// 	return actions;
// };

/**
 * Invokes the block's module to get a list of inputs (data sources) 
 * @param recipeId 
 * @param blockId 
 */
// const getBlockInputs = (recipeId: string, blockId: string): ReadonlyArray<BlockInput> => {
// 	const block = findBlockInRecipe(recipeId, blockId);
// 	if (!block) { throw new Error(`Block ${blockId} does not exist in recipe ${recipeId}`); }

// 	const blockModule = BlocksManager.getThingWebProcessor(block.id, block.version);
// 	const inputs = typeof blockModule.getInputs === 'function' && blockModule.getInputs({ ...block.state });
// 	return inputs || [];
// };

/**
 * Processes executeAction responses, sending the resulting response to any child widget connected
 * to the output ports of an Action widget.
 * @param widgetId the id of the widget in the recipe that received the event or null if the event was triggered
 * directly.
 * @param thingRecipeId the id of the Thing in the recipe.
 * @param thingDbId the id of the Thing in the database.
 * @param thingVersion the version number of the Thing.
 * @param recipeId the id of the recipe in the pool.
 * @param response the response from the `executeAction` method
 * @param data the original event data used to trigger the 'executeAction' method
 */
// const handleWidgetExecuteActionResponse = async (
// 	widgetId: string,
// 	thingRecipeId: string,
// 	thingDbId: string,
// 	thingVersion: string,
// 	recipeId: string,
// 	response: Promise<ExecuteActionResponse>,
// 	data: Data
// ): Promise<void> => {
// 	return LogicProcessor.handleExecuteActionResponse(
// 		widgetId,
// 		thingRecipeId,
// 		thingDbId,
// 		thingVersion,
// 		recipeId,
// 		response,
// 		data
// 	);
// };

/**
 * Creates an interrupt to call the given widget at the given time.
 * Mostly used by the Block widget.
 * @param targetWidgetId the id of the widget to invoke
 * @param sourceWidgetId the id of the widget that is registering the interrupt
 * @param thingId the id of the thing in the recipe
 * @param recipeId the id of the recipe in the pool
 * @param interval the elapsed in ms to wait before invoking the widget
 * @returns the id of the interrupt id
 */
const addIntervalInterrupt = (config: {
	targetWidgetId: string,
	thingId: string,
	recipeId: string,
	sourceWidgetId: string,
	portName: string,
	interval: number,
}): string => {
	const { targetWidgetId, thingId, recipeId, sourceWidgetId, portName, interval } = config;
	return kemuCore.registerInterrupt(
		{
			type: 'interval',
			recipePoolId: recipeId,
			thingId,
			targetWidgetId,
			sourceWidgetId,
			portName: portName,
			config: { interval },
		}
	);
};

/**
 * Stops and removes an existing interrupt
 * @param interruptId the id of the interrupt generated when calling `addIntervalInterrupt`
 * @returns true if the interrupt was successfully removed.
 */
const cancelInterrupt = (interruptId: string): boolean => {
	return kemuCore.cancelInterrupt(interruptId);
};

interface ChildBlockCustomInfo {
	blockDbId: string;
	// same as `key`
	blockRecipeId: string;
	canvasLabel?: string;
	name: string;
	type: ThingType;
	category: ThingCategory;
	version: string;
}

export type ChildBlockCustomInfoWithActions = Record<string, ChildBlockCustomInfo & {actions: ReadonlyArray<BlockAction>}>;
type ChildBlockCustomInfoWithInputs = Record<string, ChildBlockCustomInfo & {customInputs: ReadonlyArray<BlockInput>}>;


/**
 * Invokes all the child block's modules attached to the given parent block
 * and query their actions. It returns a dictionary where the keys are the child block's recipeId
 * @param recipeId the id of the recipe in the pool
 * @param parentBlock the id of the block in the recipe (`recipeId`)
 */
// const getChildBlocksActions = async (recipeId: string, parentBlock: string): Promise<ChildBlockCustomInfoWithActions> => {
// 	const recipe = findRecipe(recipeId);
// 	if (!recipe) { throw new Error(`Recipe [${recipeId} does not exist]`); }

// 	const block = findBlockInRecipe(recipeId, parentBlock);
// 	if (!block) { throw new Error(`Block ${parentBlock} does not exist in recipe ${recipeId}`); }

// 	const childrenBlockIds = block.children.map(child => child.blockId);

// 	// Get all the child modules and retrieve their actions
// 	const actionPromises = childrenBlockIds.map(blockId =>
// 		BlocksManager.getThingWebProcessor(recipe.blocks[blockId].id, recipe.blocks[blockId].version)
// 		.getActions(Object.freeze({ ...recipe.blocks[blockId].state }), true)
// 	);

// 	// Wait for all requests to complete (most of them will be sync btw)
// 	// 03/Jun/2021 TODO: This is not needed since `getActions` is no longer async
// 	// remove promises after all clients have been migrated to the latest version
// 	const actions = await Promise.all(actionPromises);

// 	// Build a proper response
// 	const response = childrenBlockIds.reduce((prev, currentBlockRecipeId, index) => {
// 		return {
// 			...prev,
// 			[currentBlockRecipeId]: {
// 				version: recipe.blocks[currentBlockRecipeId].version,
// 				blockDbId: recipe.blocks[currentBlockRecipeId].id,
// 				blockRecipeId: currentBlockRecipeId,
// 				canvasLabel: recipe.blocks[currentBlockRecipeId].canvas.label,
// 				category: recipe.blocks[currentBlockRecipeId].category,
// 				name: recipe.blocks[currentBlockRecipeId].name,
// 				type: recipe.blocks[currentBlockRecipeId].type,
// 				actions: actions[index]
// 			}
// 		};
// 	}, {} as ChildBlockCustomInfoWithActions);

// 	// Return a copy to prevent altering the original ones
// 	return JSON.parse(JSON.stringify(response));
// };

/**
 * Searches for all 'input' type gates of all child blocks attached to the given parent
 * and returns a dictionary with their dynamic inputs, where the key represents the child block's recipeId
 * @param recipeId the id of the recipe in the pool
 * @param parentBlock the id of the block in the recipe (`recipeId`)
 */
// const getChildBlocksDynamicInputs = (recipeId: string, parentBlock: string): ChildBlockCustomInfoWithInputs => {
// 	const recipe = findRecipe(recipeId);
// 	if (!recipe) { throw new Error(`Recipe [${recipeId} does not exist]`); }

// 	const block = findBlockInRecipe(recipeId, parentBlock);
// 	if (!block) { throw new Error(`Block ${parentBlock} does not exist in recipe ${recipeId}`); }

// 	const response: ChildBlockCustomInfoWithInputs = {};

// 	for (const childBlockRef of block.children) {
// 		const child = recipe.blocks[childBlockRef.blockId];
// 		let customInputs: BlockInput[] = [];
// 		for (const gateId in child.gates) {
// 			if (child.gates[gateId].type === WidgetType.input) {
// 				const inputGateState = <InputWidgetState> child.gates[gateId].state;
// 				if (inputGateState.customInputs?.length) {
// 					customInputs = [...customInputs, ...inputGateState.customInputs];
// 				}
// 			}
// 		}

// 		if (customInputs.length) {
// 			response[child.recipeId] = {
// 				version: child.version,
// 				blockDbId: child.id,
// 				blockRecipeId: child.recipeId,
// 				canvasLabel: child.canvas.label,
// 				category: child.category,
// 				name: child.name,
// 				type: child.type,
// 				customInputs
// 			};
// 		}
// 	}

// 	return JSON.parse(JSON.stringify(response));
// };


/**
 * Invokes the next gate in the sequence. Mostly used by 'Play' gates.
 * @param recipePoolId id of the recipe in the pool. Note that this is NOT the database id.
 * @param thingRecipeId id of the Thing in the recipe (`recipeId` property).
 * @param thingDbId id of the Thing in the database.
 * @param thingVersion version number of the Thing.
 * @param currentGateId id of the gate from the recipe that is generating the action.
 * @param sourcePortName name of the source that is originating the event
 * @param originalEvent original event that started the reaction.
 * @param data a valid Data type to pass to the next gate
 */
const invokeNextGate = (config: {
	recipePoolId: string,
	thingRecipeId: string,
	thingDbId: string,
	thingVersion: string,
	currentGateId: string,
	sourcePortName: string,
	originalEvent: Data,
	data: Data,
	currentPath: string[],
	eventId: number | null,
}): Promise<void> => {
	const { recipePoolId, thingRecipeId, thingDbId, thingVersion, currentGateId, sourcePortName, originalEvent, data, currentPath, eventId } = config;
	return kemuCore.triggerNextGate({
		recipePoolId,
		thingRecipeId,
		thingDbId,
		thingVersion,
		currentGateId,
		sourcePortName,
		originalEvent,
		data,
		currentPath,
		eventId,
	});
};

/**
 * Invokes the widget's processor 'onParentEvent' method.
 * @param widgetId id of the widget in the recipe to invoke.
 * @param thingId id of the thing in the recipe.
 * @param recipeId id of the recipe in the pool.
 * @param targetPortName name of the port the widget received the event on. This is not verified
 * so it does not have to be an actual widget's input port.
 * @param data the raw data to pass to the widget.
 */
const invokeWidgetProcessor = (
	widgetId: string,
	thingId: string,
	recipeId: string,
	targetPortName: string,
	data: PartialData,
): Promise<void> => {
	return kemuCore.triggerWidgetProcessor(
		widgetId, thingId, recipeId, targetPortName, data
	);
};

/**
 * Used by blocks when a data source or canvas instance has produced data that needs to be consumed
 * by gates.
 * @param recipePoolId 
 * @param blockRecipeId 
 * @param data 
 */
// const consumeOwnData = async (recipePoolId: string, blockRecipeId: string, data: {[k: string]: unknown}): Promise<void> => {
// 	await kemuCore.consumeData(data, [blockRecipeId], recipePoolId);
// };

/**
 * Returns a shallow copy of the block state.
 * WARNING: Modifying non-primitive properties of the object 
 * WILL mutate the state of the block
 * @param blockId 
 * @param recipeId 
 */
const getBlockState = (blockId: string, recipeId: string): Readonly<BlockState> => {
	// Read the initial state from the store
	const recipe = findRecipe(recipeId);
	if (!recipe) { throw new Error(`Recipe [${recipeId} does not exist]`); }

	const block = recipe.blocks[blockId];
	return { ...block.state };
};

/**
 * Returns a shallow copy of the block state.
 * WARNING: Modifying non-primitive properties values of the object 
 * WILL mutate the state of the block.
 * @param gateId the id of the gate in the block
 * @param blockId id of the block in the recipe (`recipeId` property).
 * @param recipeId id of the recipe in the pool. Note that this is NOT the database id.
 */
const getWidgetState = <T=GateState>(gateId: string, blockId: string, recipeId: string): Readonly<T> => {
	// Read the initial state from the store
	const gate = findWidgetInRecipe(recipeId, blockId, gateId);
	if (!gate) { throw new Error(`Gate ${gateId} not found in block ${blockId} and recipe ${recipeId}`); }

	return { ...(gate.state as unknown as T) };
};

/**
 * @deprecated use `getWidgetState` instead
 */
const getGateState = getWidgetState;

type GateParent = Omit<GateChild, 'childId'> & {parentId: string};
/**
 * Searches for all the widgets in the block and returns a list of widgets that
 * have the given widgetId as a child
 * @param widgetId the id of the widget to search parents for
 * @param blockId id of the block in the recipe (`recipeId` property).
 * @param recipeId id of the recipe in the pool. Note that this is NOT the database id.
 */
const getWidgetParents = (config: {
	widgetId: string,
	blockId: string,
	recipeId: string,
}): GateParent[] => {
	const { widgetId, blockId, recipeId } = config;
	const block = findThingInRecipe(recipeId, blockId);
	if (!block) { throw new Error(`Block ${blockId} does not exist in recipe ${recipeId}`); }

	const parents: GateParent[] = [];

	for (const key in block.gates) {
		const gate = block.gates[key];
		const childrenList = gate.children.filter(child => child.childId === widgetId);
		if (childrenList) {
			childrenList.forEach(child => {
				parents.push({
					parentId: gate.id,
					sourcePort: child.sourcePort,
					targetPort: child.targetPort,
				});
			});
		}
	}

	return parents;
};


/**
 * Removes all connections from the given widget port, either as a parent or as a child.
 * NOTE: This method doe not perform any UI updates.
 * @param recipeId the id of the recipe in the pool
 * @param thingId the id of the thing in the recipe
 * @param widgetId the id of the widget to remove connections from
 * @param portName the name of the port to remove the connections from
 */
const removeWidgetPortConnections = (recipeId: string, thingId: string, widgetId: string, portName: string): {
	removedParents: {
		widgetId: string;
		portName: string;
	}[];

	removedChildren: {
		widgetId: string;
		portName: string;
	}[];
} => {
	const thing = findThingInRecipe(recipeId, thingId);
	if (!thing) { throw new Error(`Thing ${thingId} not found in recipe ${recipeId}`); }

	const targetWidget = thing.gates[widgetId];
	if (!targetWidget) { throw new Error(`Widget ${widgetId} not found in Thing ${thingId} and recipe ${recipeId}`); }

	const removedParents: {
		widgetId: string;
		portName: string;
	}[] = [];

	const removedChildren: {
		widgetId: string;
		portName: string;
	}[] = [];


	// Find all parents that have the given widget as a child
	for (const key in thing.gates) {
		const widget = thing.gates[key];
		// Skip the widget itself
		if (widget.id === widgetId) { continue; }
		// Check if the widget has the given widget as a child
		const withoutTargetWidget = widget.children.filter((child) => {
			const decodedTargetPort = decodeWidgetPortIdentifier(child.targetPort);
			const isLinkedToPort = child.childId === widgetId && decodedTargetPort.portName === portName;
			if (isLinkedToPort) {
				removedParents.push({ widgetId: widget.id, portName: decodedTargetPort.portName });
			}
			return !isLinkedToPort;
		});

		widget.children = withoutTargetWidget;
	}

	// Now find all child connections to the given port and remove them
	const withoutConnections = targetWidget.children.filter(child => {
		const decodedSourcePort = decodeWidgetPortIdentifier(child.sourcePort);
		const isLinkedToPort = decodedSourcePort.portName === portName;
		if (isLinkedToPort) {
			removedChildren.push({ widgetId: child.childId, portName: decodedSourcePort.portName });
		}
		return !isLinkedToPort; //decodedSourcePort.portName !== portName;
	});

	targetWidget.children = withoutConnections;

	return { removedParents, removedChildren };
};

/**
 * Returns the widget's information in the recipe.
 * 
 * NOTE: This method is meant to be used at high data-rates and to avoid making costly deep copies
 * of the object, it was decided to implement it this way, and give it a name to remind future
 * developers that the returning object SHOULD NOT be modified.
 * 
 * @param recipeId the recipe pool id
 * @param thingRecipeId the id of the Thing in the recipe (recipeId)
 * @param widgetId the id of the widget in the block.
 * @returns a shallow read-only (statically marked as read-only although not enforced in runtime) 
 * copy of the widget from the recipe. WARNING: changing inner properties WILL change the original 
 * object.
 */
 const dangerouslyGetWidgetInfo = (recipeId: string, thingRecipeId: string, widgetId: string): Readonly<RecipeWidget> | null => {
	const widget = findWidgetInRecipe(recipeId, thingRecipeId, widgetId);
	if (!widget) { throw new Error(`Gate ${widgetId} not found in block ${thingRecipeId} and recipe ${recipeId}`); }

	return {
		...widget
	};
};


const toggleReturnOriginalEvent = (widgetId: string, blockId: string, recipeId: string): RecipeWidget => {
	const gate = findWidgetInRecipe(recipeId, blockId, widgetId);
	if (!gate) { throw new Error(`Gate ${widgetId} not found in block ${blockId} and recipe ${recipeId}`); }

	gate.returnOriginalEvent = !gate.returnOriginalEvent;
	return gate;
};

/**
 * @returns the position of the draggable area universally. In other words, regardless of whether
 * inside of a thing or not.
 */
const getDraggableCanvasPosition = (): Position => {
	const canvasContainer = document.querySelector(`.${LM_CANVAS_CONTAINER_CLASS}`);
	const draggableEl = document.querySelector(`.${CANVAS_DRAGGABLE_CLASS}.${LOGIC_MAPPER_CANVAS_CLASS}`);
	if (!draggableEl || ! canvasContainer) { return { x: 0, y: 0 }; }

	const childPos = draggableEl.getBoundingClientRect();
	const parentPos = canvasContainer.getBoundingClientRect();

	const top = childPos.top - parentPos.top;
	const left = childPos.left - parentPos.left;
	// const right = childPos.right - parentPos.right;
	// const bottom = childPos.bottom - parentPos.bottom;

	return {
		x: left,
		y: top
	};

};


/**
 * Returns a list of secret names that are required by the recipe.
 * @param recipePoolId 
 * @returns 
 */
const getRecipeRequiredSecrets = (recipePoolId: string): WidgetSecretConfig[] => {
	const recipe = findRecipe(recipePoolId);
	if (!recipe) { throw new Error(`Recipe [${recipePoolId} does not exist]`); }
	const secrets: WidgetSecretConfig[] = [];
	for (const thingId in recipe.blocks) {
		const thing = recipe.blocks[thingId];
		for (const widgets in thing.gates) {
			const widget = thing.gates[widgets];
			if (widget.secrets) {
				for (const secretName in widget.secrets) {
					// Skip if the secret is already in the list
					if (secrets.find(secret => secret.name === secretName)) { continue; }
					secrets.push(widget.secrets[secretName]);
				}
			}
		}
	}

	return secrets;
};

export {
	getRecipeRequiredSecrets,
	registerChildBlock,
	removeChildBlock,
	registerChildGate,
	removeChildGate,
	addGateToBlock,
	// duplicateGate,
	removeGate,
	removeWidget,
	setCanvasInfo,
	setBlockCanvasInfo,
	setGateCanvasInfo,
	getBlockCanvasInfo,
	getGateCanvasInfo,
	getWidgetCanvasInfo,
	getCanvasInfo,
	regenerateIds,
	prepareRecipeForSaving,
	updateRecipeCacheMeta,
	getRecipeStorage,
	serializeGlobalVariables,
	deserializeGlobalVariables,
	// removeBlock,
	// addBlock,
	// duplicateBlock,
	// getBlockActions,
	// getBlockInputs,
	// getChildBlocksActions,
	// getChildBlocksDynamicInputs,
	invokeNextGate,
	// consumeOwnData,
	getBlockState,
	addToStorageUnit as addToStorage,
	getStorageUnitFromKey,
	// getStorageUnitFromKey,
	removeStorageUnit,
	getBlockStorageKeys,
	removeIdMarker,
	getIdMarker,
	isGateAChildAtPort,
	// getUniqueGatePortId,
	// getUniqueWidgetPortId,
	getGateState,
	getWidgetState,
	// decodeGatePortId,
	getWidgetParents,
	toggleReturnOriginalEvent,
	getChildrenWidgets,
	// handleWidgetExecuteActionResponse,
	disableWidget,
	getWidgetDisabled,
	addIntervalInterrupt,
	cancelInterrupt,
	dangerouslyGetWidgetInfo,
	addCustomWidgetToThing,
	getDraggableCanvasPosition,
	editCustomWidgetState,
	getCustomWidgetState,
	resetWidgetGroupFields,
	getCustomWidgetInnerWidgets,
	addCustomWidgetFromTemplate,
	getWidgetsInBlock,
	getWidgetsInThing,
	addWidgetsFromStringMap,
	invokeWidgetProcessor,
	generateUniqueWidgetId,
	getEntityDomEl,
	cloneStorageUnit,
	getRandomPortName,
	removeWidgetPortConnections,
	removeOrphanConnections,
	removeOrphanWidgets,
	getGroupInnerWidgets,

	addWidgetToThing,
  addWidgetWithIdToThing,
	getDefaultWidgetPosition,
};
